import { createRoot } from "react-dom/client";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { alpha, ThemeProvider } from "@mui/material/styles";
import { getBrandThemes, brandPrefersDark } from "../theme";
import { getActiveBrand } from "../branding/activeBrand";

// Store the current dark mode value
let currentDarkMode = false;

// Function to update the dark mode value
export const updateConfirmationDialogDarkMode = (isDark: boolean) => {
  currentDarkMode = isDark;
};

// Initialize with the current dark mode value from localStorage
const savedDarkMode = localStorage.getItem("darkMode.v2");
if (savedDarkMode) {
  currentDarkMode = JSON.parse(savedDarkMode);
}

interface ConfirmationDialogProps {
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

function ConfirmationDialog({
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
}: ConfirmationDialogProps) {
  const brand = getActiveBrand();
  const themes = getBrandThemes(brand.key);
  // Respect a brand that ships dark (CerebraLinked) even when the user's dark-mode toggle
  // (localStorage) is off — otherwise this imperatively-mounted dialog renders light inside
  // the dark app.
  const dark = currentDarkMode || brandPrefersDark(brand.key);
  const theme = dark ? themes.dark : themes.light;

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open
        onClose={onCancel}
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-description"
      >
        <DialogTitle id="confirmation-dialog-title">{title}</DialogTitle>
        <DialogContent>
          <Typography id="confirmation-dialog-description">
            {message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onConfirm} color="primary" variant="contained">
            {confirmText}
          </Button>
          <Button
            onClick={onCancel}
            variant="outlined"
            sx={{
              color: "text.primary",
              borderColor: alpha(theme.palette.text.primary, 0.35),
              "&:hover": {
                borderColor: alpha(theme.palette.text.primary, 0.6),
              },
            }}
          >
            {cancelText}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

// Create a wrapper function that manages the dialog state
function createConfirmationDialog({
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
}: ConfirmationDialogProps) {
  const div = document.createElement("div");
  document.body.appendChild(div);

  const root = createRoot(div);

  const cleanup = () => {
    root.unmount();
    div.remove();
  };

  const handleConfirm = () => {
    onConfirm?.();
    cleanup();
  };

  const handleCancel = () => {
    onCancel?.();
    cleanup();
  };

  root.render(
    <ConfirmationDialog
      title={title}
      message={message}
      confirmText={confirmText}
      cancelText={cancelText}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />,
  );

  return cleanup;
}

export default createConfirmationDialog;
